import get from 'lodash/get';
import sortBy from 'lodash/sortBy';
import uniq from 'lodash/uniq';

import {
  Issue,
  Project,
  ReportFetchResultPagination,
  TestCaseInCycle,
  TestCycle,
  TestCycleDTO,
  TestExecution,
  User,
  Version,
} from '@qality/common-model';
import {
  TestCycleForExecutionResponse,
  TestCycleRequestData,
} from '@qality/common-model/src/TestCycle';
import {
  StorageOfUniqueServices,
  AxiosWrapperService as axios,
} from '@qality/common-service';
import { Utils } from '@qality/common-utils';

import PermissionService from './PermissionService';
import ProjectService from './ProjectService';
import { TestCycleFilters } from './TestCycleFilters';
import UserService from './UserService';
import VersionService from './VersionService';

const NOT_FOUND_STATUS = 404;
const PROJECTS = {};
const VERSIONS = {};

export default {
  async fetchTestCycle(testCycleId: number): Promise<TestCycle> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CYCLES +
        '/' +
        testCycleId
    );

    try {
      const { data } = await axios.get(url);
      const [testCycle, testCases] = await Promise.all([
        createTestCycleFromResponse(data),
        fetchIssueDetails(data.testCases),
      ]);

      if (!testCycle) {
        return null;
      }

      testCycle.testCases = testCases;
      return testCycle;
    } catch (e) {
      if (e?.response?.status === NOT_FOUND_STATUS) {
        return null;
      } else {
        throw e;
      }
    }
  },

  async updateTestCycle(
    id: TestCycle['id'],
    testCycle: TestCycleRequestData
  ): Promise<void> {
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CYCLES +
        `/${id}`
    );

    await axios.patch(url, testCycle, {
      headers: {
        'Content-Type': 'application/json',
      },
    });
  },

  async fetchTestCycles(
    nextPage: number,
    filters?: TestCycleFilters,
    shouldFetchPermissions = true,
    shouldFetchVersions = true,
    elementsPerPage = 10
  ): Promise<{
    pagination: ReportFetchResultPagination;
    testCycles: Array<TestCycle>;
  }> {
    let params = `page=${nextPage}&size=${elementsPerPage}`;
    if (filters) {
      Object.keys(filters).forEach((key) => {
        params += `&${key}=${filters[key]}`;
      });
    }

    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants').URL_TEST_CYCLES,
      params
    );

    const response = (await axios.get(encodeURI(url))).data;
    const responseTestCycles = get(response, 'content', []);
    const testCycles = await fetchTestCyclesDetails(
      responseTestCycles,
      shouldFetchPermissions,
      shouldFetchVersions
    );

    return {
      pagination: {
        currentPage: get(
          response,
          'pageable.pageNumber',
          nextPage ? nextPage : 0
        ),
        pageSize: get(response, 'pageable.pageSize', 10),
        totalPages: get(response, 'totalPages', 1),
        totalElements: get(response, 'totalElements', 1),
        last: get(response, 'last', true),
      },
      testCycles,
    };
  },

  async fetchTestCyclesByIds(
    testCycleIds: Array<TestCycle['id']>
  ): Promise<Array<TestCycle>> {
    return Promise.all(testCycleIds.map((id) => this.fetchTestCycle(id)));
  },

  async fetchTestCyclesForExecution(
    nextPage: number,
    testCaseId: string,
    name?: string
  ): Promise<TestCycleForExecutionResponse> {
    let params = `page=${nextPage}&testCaseId=${testCaseId}`;
    if (name) {
      params += `&name=${name}`;
    }
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin +
        StorageOfUniqueServices.get('APIConstants')
          .URL_TEST_CYCLES_FOR_EXECUTION,
      params
    );
    const response = (await axios.get(url)).data;
    let testCycles = get(response, 'content', []);

    const projectIds: Array<string> = testCycles.map(
      (testCycle) => testCycle.projectId
    );
    const projectMap: { [projectId: string]: Project } =
      await ProjectService.fetchProjects(projectIds);

    testCycles = testCycles.map((testCycle) => {
      testCycle.projectKey = projectMap[testCycle.projectId]?.key;
      testCycle.projectAvatarUrl =
        projectMap[testCycle.projectId]?.avatarUrls['24x24'];
      return testCycle;
    });

    return {
      content: testCycles,
    };
  },

  async fetchTestCaseInCycle(
    testCaseInCycleId: number,
    testCycleId: number
  ): Promise<{
    id: number;
    testCaseName: string;
    testExecution: TestExecution;
  }> {
    const link =
      StorageOfUniqueServices.get('APIConstants').URL_TEST_CYCLES +
      `/${testCycleId}/testCycleAssignments/${testCaseInCycleId}`;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link
    );
    const response = (await axios.get(url)).data;
    return response;
  },

  async fetchUnexecutedTestCaseInCycleId(
    testCaseId: number | string,
    testCycleId: number
  ): Promise<number> {
    const link =
      StorageOfUniqueServices.get('APIConstants')
        .URL_TEST_CYCLES_FOR_EXECUTION + `/${testCaseId}/${testCycleId}`;
    const url = await StorageOfUniqueServices.get(
      'AuthenticateService'
    ).authenticateByJWT(
      StorageOfUniqueServices.get('WindowOriginService').origin + link
    );
    const response = (await axios.get(url)).data;
    return response?.testCaseInCycleId;
  },
};

async function fetchTestCyclesDetails(
  responseTestCycles: Array<TestCycleDTO>,
  shouldFetchPermissions,
  shouldFetchVersions
): Promise<Array<TestCycle>> {
  const testCycles = [];

  let projectIds = responseTestCycles.map((testCycle) => {
    return testCycle.projectId;
  });

  projectIds = uniq(projectIds);

  let versionIds = shouldFetchVersions
    ? responseTestCycles
        .filter((testCycle) => {
          return !isTestCycleWithoutVersion(testCycle);
        })
        .map((testCycle) => {
          return testCycle.versionId;
        })
    : [];

  versionIds = uniq(versionIds);

  const [projectAndVersionMaps, editPermissionsMap] = await Promise.all([
    fetchProjectsAndVersions(projectIds, versionIds),
    PermissionService.getEditPermissionForEachProject(
      shouldFetchPermissions ? projectIds : []
    ),
  ]);

  responseTestCycles.forEach((testCycle) => {
    const project = projectAndVersionMaps.projectMap[testCycle.projectId];

    const version = isTestCycleWithoutVersion(testCycle)
      ? null
      : projectAndVersionMaps.versionMap[testCycle.versionId];

    if (project) {
      testCycles.push(
        new TestCycle(
          testCycle.id,
          testCycle.name,
          testCycle.testCases,
          {
            id: project.id,
            name: project.name,
            iconUrl: project.avatarUrls['24x24'],
            key: project.key,
          },
          version ? { id: version.id, name: version.name } : null,
          testCycle.statuses,
          testCycle.createdDate,
          testCycle.dueDate,
          testCycle.comment,
          editPermissionsMap[project.id] || false,
          testCycle.isClosed,
          testCycle.numberOfTestCases,
          testCycle.testCaseExistInCycle
        )
      );
    }
  });
  return testCycles;
}

async function fetchProjectsAndVersions(
  projectIds: Array<string>,
  versionIds: Array<string>
): Promise<{
  projectMap: { [projectId: string]: Project };
  versionMap: { [versionId: number]: Version };
}> {
  const projectPromises = [];
  const versionPromises = [];

  const projectMap = {};
  const versionMap = {};

  projectIds.forEach((projectId) => {
    if (PROJECTS[projectId]) {
      projectMap[projectId] = PROJECTS[projectId];
    } else {
      projectPromises.push(
        ProjectService.getProject(projectId).catch((e) => {
          if (e.xhr?.status === NOT_FOUND_STATUS) {
            return null;
          }
          throw new Error(`Could not fetch project with id ${projectId}!`);
        })
      );
    }
  });

  versionIds.forEach((versionId) => {
    if (VERSIONS[versionId]) {
      versionMap[versionId] = VERSIONS[versionId];
    } else {
      versionPromises.push(VersionService.getVersion(versionId));
    }
  });

  const projects = await Promise.all(projectPromises);
  const versions = await Promise.all(versionPromises);

  projects.forEach((project) => {
    if (project) {
      projectMap[project.id] = project;
      PROJECTS[project.id] = project;
    }
  });

  versions.forEach((version) => {
    versionMap[version.id] = version;
    VERSIONS[version.id] = version;
  });
  return { projectMap, versionMap };
}

async function fetchProjectAndVersion(
  testCycle: TestCycleDTO
): Promise<{ project: Project; version: Version }> {
  const promises: Array<Promise<Project | Version>> = [];
  promises.push(
    ProjectService.getProject(testCycle.projectId).catch((e) => {
      if (e.xhr?.status === NOT_FOUND_STATUS) {
        return null;
      }
      throw new Error(
        `Could not fetch project with id ${testCycle.projectId}!`
      );
    })
  );

  if (!isTestCycleWithoutVersion(testCycle)) {
    promises.push(VersionService.getVersion(testCycle.versionId));
  }
  const [project, version] = await Promise.all(promises);
  // @ts-expect-error <no idea why how to handle promise.all mixing types>
  return { project, version };
}

async function createTestCycleFromResponse(testCycle) {
  const { project, version } = await fetchProjectAndVersion(testCycle);

  if (!project || project?.archived) {
    return null;
  } else {
    const canEdit = (
      await PermissionService.hasProjectPermissions('' + project.id, [
        'EDIT_ISSUES',
      ])
    )['EDIT_ISSUES'];
    return new TestCycle(
      testCycle.id,
      testCycle.name,
      testCycle.testCases,
      {
        id: project.id,
        name: project.name,
        iconUrl: project.avatarUrls['24x24'],
        key: project.key,
      },
      version ? { id: version.id, name: version.name } : null,
      testCycle.statuses,
      testCycle.createdDate,
      testCycle.dueDate,
      testCycle.comment,
      canEdit,
      testCycle.isClosed,
      testCycle.numberOfTestCases,
      testCycle.testCaseExistInCycle
    );
  }
}

//TODO: handle typescript errors in next PR.
async function fetchIssueDetails(
  testCases: Array<TestCaseInCycle>
): Promise<Array<TestCaseInCycle>> {
  const testCaseIds = testCases.map((testCase) => testCase.testCaseId);
  const bugIds = testCases
    .map((testCase) => testCase.issuesLinkedToExecution)
    .flat(Infinity)
    .filter((bugId) => !Utils.isNilOrEmpty(bugId));
  let testExecutionAssignees = testCases
    .filter(
      (testCase) =>
        !Utils.isNilOrEmpty(testCase.testExecution) &&
        !Utils.isNilOrEmpty(testCase.testExecution.executionAssignee)
    )
    .map((testCase) => testCase.testExecution.executionAssignee);

  testExecutionAssignees = Array.from(new Set(testExecutionAssignees));

  const issuesIds = [...testCaseIds, ...bugIds].flat();
  const fields = ['summary', 'issuetype', 'status'];
  const [issues, users] = await Promise.all([
    await StorageOfUniqueServices.get('JQLSearchService').bulkFetchIssuesByIds({
      ids: issuesIds,
      fields,
      maxResults: 50,
    }),
    UserService.fetchAssignees(testExecutionAssignees),
  ]);

  let newTestCases = await Promise.all(
    testCases.map(async (testCase) => {
      testCase.testCase = findIssue(issues, testCase.testCaseId);
      if (!Utils.isNilOrEmpty(testCase.testExecution)) {
        testCase.testExecution.executionAssignee = findUser(
          users,
          testCase.testExecution.executionAssignee
        );
      }

      if (!Utils.isNilOrEmpty(testCase.issuesLinkedToExecution)) {
        testCase.issuesLinkedToExecution = testCase.issuesLinkedToExecution
          .map((bugId) => findIssue(issues, bugId.toString()))
          .filter((bug) => !Utils.isNilOrEmpty(bug));
      }

      return testCase;
    })
  );
  newTestCases = sortBy(newTestCases, ['position', 'id']);
  return newTestCases;
}

function isTestCycleWithoutVersion(testCycle: TestCycleDTO): boolean {
  return (
    Utils.isNilOrEmpty(testCycle.versionId) || testCycle.versionId === 'null'
  );
}

function findIssue(issues: Array<Issue>, issueId: number): Issue {
  return issues.find((issue) => issue.id == issueId);
}

function findUser(users: Array<User>, userId: string): User {
  return users.find((user) => user.getIdentifier() == userId);
}
